import { memo, useEffect, useRef } from "react";
import {
  PARTICLE_DISTANCE_RANGE,
  PARTICLE_DURATION_RANGE,
  PARTICLE_GRAVITY,
  PARTICLE_MIN_DISTANCE,
  PARTICLE_MIN_DURATION,
} from "../constants";

interface ParticleProps {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  onDone: () => void;
}

function ParticleComponent({ x, y, vx, vy, color, size, onDone }: ParticleProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const start = performance.now();
    const duration = PARTICLE_MIN_DURATION + Math.random() * PARTICLE_DURATION_RANGE;
    const distance = PARTICLE_MIN_DISTANCE + Math.random() * PARTICLE_DISTANCE_RANGE;
    let raf: number;

    function tick(now: number) {
      const t = (now - start) / duration;
      if (t >= 1) {
        onDone();
        return;
      }
      const eased = 1 - Math.pow(1 - t, 2);
      const dx = vx * eased * distance;
      const dy = vy * eased * distance + t * t * PARTICLE_GRAVITY;
      el!.style.transform = `translate(${dx}px, ${dy}px) scale(${1 - t})`;
      el!.style.opacity = String(1 - t);
      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        pointerEvents: "none",
        zIndex: 50,
        willChange: "transform, opacity",
      }}
    />
  );
}

export const Particle = memo(ParticleComponent);
export default Particle;
